"use client";

import { BookOpen, Cpu, Users, Siren, Send, BarChart3, Brain, Globe, type LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { coreFeatures, advancedFeatures, deploymentTiers } from "@/lib/data/platform-features";

const iconMap: Record<string, LucideIcon> = {
  BookOpen, Cpu, Users, Siren, Send, BarChart3, Brain, Globe,
};
const tierIconMap: Record<string, LucideIcon> = {
  Compass: iconMap.Cpu ?? Cpu,
  TrendingUp: iconMap.BarChart3 ?? BarChart3,
  Shield: iconMap.Siren ?? Siren,
  Award: iconMap.Brain ?? Brain,
};

export function CoreFeatures() {
  return (
    <Section id="core-features" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/4 right-1/3 h-80 w-80 rounded-full bg-[var(--color-cyan)] opacity-15 blur-[120px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">Core Features</span>
            <h2 className="mt-3">The Capabilities That Define Autonomous Perception Management</h2>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {coreFeatures.map((f, i) => {
            const Icon = iconMap[f.icon] ?? BookOpen;
            return (
              <Reveal key={f.name} delay={i * 50}>
                <div
                  className="glass rounded-2xl p-5 h-full"
                  style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${f.accent}) 12%, transparent)` }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="grid place-items-center h-10 w-10 rounded-lg"
                      style={{
                        background: `color-mix(in oklch, var(--color-${f.accent}) 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${f.accent}) 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-4 w-4" style={{ color: `var(--color-${f.accent})` }} />
                    </span>
                    <span className="eyebrow-tiny font-bold" style={{ color: `var(--color-${f.accent})` }}>{f.number}</span>
                  </div>
                  <h3 className="text-base">{f.name}</h3>
                  <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{f.description}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={300}>
          <div className="mt-16">
            <div className="max-w-3xl">
              <span className="eyebrow-tiny text-[var(--color-emerald)]">Advanced Features</span>
              <h3 className="mt-3">Premium Capabilities for Enterprise-Grade Perception Management</h3>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {advancedFeatures.map((f, i) => {
                const Icon = iconMap[f.icon] ?? Cpu;
                return (
                  <div
                    key={f.name}
                    className="glass rounded-2xl p-5 h-full"
                    style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${f.accent}) 12%, transparent)` }}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="grid place-items-center h-10 w-10 rounded-lg shrink-0"
                        style={{
                          background: `color-mix(in oklch, var(--color-${f.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${f.accent}) 35%, transparent)`,
                        }}
                      >
                        <Icon className="h-4 w-4" style={{ color: `var(--color-${f.accent})` }} />
                      </span>
                      <h3 className="text-base">{f.name}</h3>
                    </div>
                    <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{f.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={400}>
          <div className="mt-16">
            <div className="max-w-3xl">
              <span className="eyebrow-tiny text-[var(--color-amber)]">Deployment Tiers</span>
              <h3 className="mt-3">Structured Engagement Models for Every Requirement</h3>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {deploymentTiers.map((t, i) => {
                const Icon = tierIconMap[t.icon] ?? Cpu;
                return (
                  <div
                    key={t.tier}
                    className="glass rounded-2xl p-5 h-full"
                    style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${t.accent}) 12%, transparent)` }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span
                        className="grid place-items-center h-10 w-10 rounded-lg"
                        style={{
                          background: `color-mix(in oklch, var(--color-${t.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${t.accent}) 35%, transparent)`,
                        }}
                      >
                        <Icon className="h-4 w-4" style={{ color: `var(--color-${t.accent})` }} />
                      </span>
                      <span className="eyebrow-tiny font-bold" style={{ color: `var(--color-${t.accent})` }}>{t.tier}</span>
                    </div>
                    <h3 className="text-base">{t.name}</h3>
                    <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{t.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
